import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  useAddAffiliateNote,
  useAffiliate,
  useAffiliateNotes,
  useAffiliateStats,
  useDeleteAffiliate,
  useRestoreAffiliate,
  useSendAffiliateReport,
  useSetAffiliateCommission,
  useSubscriptionPlans,
  useSuspendAffiliate,
} from "@/hooks/useAffiliates";

import { format } from "date-fns";
import {
  Activity,
  ArrowLeft,
  Copy,
  DollarSign,
  FileText,
  MoreHorizontal,
  Plus,
  Shield,
  ShieldOff,
  ShoppingCart,
  StickyNote,
  Trash2,
  Users as UsersIcon,
} from "lucide-react";
import { useState } from "react";
import { useLocation, useRoute } from "wouter";

const getDisplayEmail = (user: AffiliateUser | null) => {
  if (!user) return "";
  return user.email;
};

function safeFormatDate(
  value: string | null | undefined,
  pattern: string,
  fallback = "—",
): string {
  if (!value) return fallback;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? fallback : format(d, pattern);
}

function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((n) => n[0]!.toUpperCase())
    .join("");
}

export default function AffiliateProfile() {
  const [, params] = useRoute("/affiliates/:id/profile");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const userId = params?.id;

  const [actionDialog, setActionDialog] = useState<{
    type: "restore" | "delete" | "suspend" | "addNote" | "setCommission" | null;
    user: AffiliateUser | null;
  }>({ type: null, user: null });
  const [actionReason, setActionReason] = useState("");

  const { data: user, isLoading } = useAffiliate(userId);
  const { data: notes = [], isLoading: notesLoading } =
    useAffiliateNotes(userId);
  const { data: stats, isLoading: statsLoading } = useAffiliateStats(userId);

  const handleActionSuccess = () => {
    setActionDialog({ type: null, user: null });
    setActionReason("");
  };

  const deleteUserMutation = useDeleteAffiliate(handleActionSuccess);
  const restoreUserMutation = useRestoreAffiliate(handleActionSuccess);
  const suspendUserMutation = useSuspendAffiliate(handleActionSuccess);
  const setCommissionMutation = useSetAffiliateCommission(handleActionSuccess);

  const addNoteMutation = useAddAffiliateNote(userId!, () => {
    setActionReason("");
    setActionDialog({ type: null, user: null });
  });

  const sendReportMutation = useSendAffiliateReport();

  const handleAction = (type: any, user: AffiliateUser) => {
    if (type === "sendReport") {
      sendReportMutation.mutate({
        affiliateId: user.id,
      });
      return;
    }
    // Pre-fill commission rate when opening setCommission dialog
    if (type === "setCommission") {
      setActionReason(String(user.affiliate?.commissionRate ?? 10));
    }
    setActionDialog({ type, user });
  };

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6 lg:p-8">
        <Button
          variant="ghost"
          onClick={() => setLocation("/affiliates")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Affiliates
        </Button>
        <p className="text-muted-foreground">Affiliate not found</p>
      </div>
    );
  }

  const commissionRate = user.affiliate?.commissionRate ?? 10;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLocation("/affiliates")}
        className="mb-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Affiliates
      </Button>

      {/* Header Card */}
      <Card className="rounded-xl overflow-hidden border-none shadow-sm shadow-primary/5">
        <CardContent className="p-0">
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="h-24 w-24 lg:h-28 lg:w-28 rounded-2xl bg-card shadow-sm flex items-center justify-center overflow-hidden border-4 border-card">
                <Avatar className="h-full w-full rounded-2xl">
                  {user.avatarUrl && (
                    <AvatarImage src={user.avatarUrl} alt={user.name ?? ""} />
                  )}
                  <AvatarFallback className="rounded-2xl bg-primary/10 text-primary text-2xl lg:text-3xl font-semibold">
                    {getInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div>
                    <h1 className="font-serif text-2xl lg:text-3xl font-semibold tracking-tight text-foreground">
                      {user.name || "—"}
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                      {getDisplayEmail(user)}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      <Badge
                        variant="secondary"
                        className="bg-primary/10 text-primary border-none"
                      >
                        Affiliate
                      </Badge>
                      <Badge
                        variant={
                          user.influencerProfile?.status === "SUSPENDED"
                            ? "destructive"
                            : "default"
                        }
                        className={
                          user.influencerProfile?.status === "SUSPENDED"
                            ? ""
                            : "bg-green-500/10 text-green-700 hover:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500/20 border-none"
                        }
                      >
                        {user.influencerProfile?.status === "SUSPENDED"
                          ? "Suspended"
                          : "Active"}
                      </Badge>

                      {user.affiliate?.affiliateCode && (
                        <div className="flex items-center gap-1.5 ml-2 bg-card/80 dark:bg-muted/40 backdrop-blur-sm px-2 py-1 rounded-md border border-border shadow-sm">
                          <span className="text-xs text-muted-foreground font-medium">
                            Code:
                          </span>
                          <code className="text-xs font-mono font-semibold text-foreground">
                            {user.affiliate.affiliateCode}
                          </code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 -mr-1 hover:bg-muted text-muted-foreground hover:text-foreground"
                            onClick={(e) => {
                              e.stopPropagation();
                              const link = user.affiliateLink || "";
                              navigator.clipboard.writeText(link);
                              toast({
                                title: "Link copied",
                                description:
                                  "Affiliate link copied to clipboard",
                              });
                            }}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {commissionRate !== null && (
                        <div className="flex items-center gap-1.5 bg-card/80 dark:bg-muted/40 px-2 py-1 rounded-md border border-border shadow-sm">
                          <span className="text-xs text-muted-foreground">
                            Commission:
                          </span>
                          <span className="text-xs font-semibold text-foreground">
                            {commissionRate}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="shadow-sm">
                        Actions <MoreHorizontal className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        onClick={() => handleAction("sendReport", user)}
                        disabled={sendReportMutation.isPending}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        {sendReportMutation.isPending
                          ? "Sending..."
                          : "Send report to mail"}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleAction("setCommission", user)}
                      >
                        <Activity className="mr-2 h-4 w-4" />
                        Set Commission
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {user.influencerProfile?.status !== "SUSPENDED" && (
                        <DropdownMenuItem
                          onClick={() => handleAction("suspend", user)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Shield className="mr-2 h-4 w-4" />
                          Suspend Account
                        </DropdownMenuItem>
                      )}
                      {user.influencerProfile?.status === "SUSPENDED" && (
                        <DropdownMenuItem
                          onClick={() => handleAction("restore", user)}
                        >
                          <ShieldOff className="mr-2 h-4 w-4" />
                          Restore Account
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => handleAction("delete", user)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete User (GDPR)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Section */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-2 lg:grid-cols-3">
        <Card className="rounded-xl border-none shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <UsersIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                {statsLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <p className="text-2xl font-semibold">
                    {stats?.totalSignups ?? 0}
                  </p>
                )}
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                  Total Signups
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-none shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <ShoppingCart className="h-5 w-5 text-primary" />
              </div>
              <div>
                {statsLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <p className="text-2xl font-semibold">
                    {stats?.totalPurchases ?? 0}
                  </p>
                )}
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                  Total Purchases
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-none shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                {statsLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <p className="text-2xl font-semibold text-green-600 dark:text-green-400">
                    ${(stats?.totalCommission ?? 0).toFixed(2)}
                  </p>
                )}
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                  Total Commission
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Account Overview */}
          <Card className="rounded-xl border-none shadow-sm">
            <CardHeader>
              <CardTitle className="font-serif text-lg">
                Account Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground mb-1">Full Name</p>
                  <p className="font-medium">{user.name || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Email Address</p>
                  <p className="font-medium">{getDisplayEmail(user) || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Joined Date</p>
                  <p className="font-medium">
                    {safeFormatDate(user.createdAt, "MMM d, yyyy", "—")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Engagement Overview */}

          {/* Support Notes */}
          <Card className="rounded-xl border-none shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="font-serif text-lg">
                Internal Notes
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleAction("addNote", user)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {notesLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : notes.length === 0 ? (
                  <div className="text-center py-8">
                    <StickyNote className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No notes for this affiliate yet.
                    </p>
                  </div>
                ) : (
                  notes.map((note) => (
                    <div
                      key={note.id}
                      className="p-3 rounded-lg bg-muted/50 space-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">
                          {note.authorUsername}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {safeFormatDate(note.createdAt, "MMM d, yyyy")}
                        </span>
                      </div>
                      <p className="text-sm">{note.content}</p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <ActionDialog
        dialog={actionDialog}
        reason={actionReason}
        onReasonChange={setActionReason}
        onClose={() => setActionDialog({ type: null, user: null })}
        onConfirm={() => {
          if (!actionDialog.user) return;
          const user = actionDialog.user;
          const uId = user.id;

          switch (actionDialog.type) {
            case "restore":
              restoreUserMutation.mutate(uId);
              break;
            case "delete":
              deleteUserMutation.mutate(uId);
              break;
            case "suspend":
              suspendUserMutation.mutate(uId);
              break;
            case "addNote":
              addNoteMutation.mutate(actionReason);
              break;
            case "setCommission":
              setCommissionMutation.mutate({
                userId: uId,
                commissionRate: Number(actionReason),
              });
              break;
          }
        }}
        isPending={
          restoreUserMutation.isPending ||
          deleteUserMutation.isPending ||
          suspendUserMutation.isPending ||
          addNoteMutation.isPending ||
          setCommissionMutation.isPending
        }
        showCommissionCalculation={actionDialog.type === "setCommission"}
      />
    </div>
  );
}

// Reusing the ActionDialog component pattern
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AffiliateUser, StripeSubscriptionPlan } from "@/lib/schema";

function CommissionCalculationPreview({
  commissionRate,
  plans,
  isLoading,
}: {
  commissionRate: number;
  plans: StripeSubscriptionPlan[] | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="mt-4 p-4 bg-muted/50 rounded-lg border">
        <Skeleton className="h-4 w-32 mb-3" />
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="mt-4 p-4 bg-muted/50 rounded-lg border">
        <p className="text-sm text-muted-foreground font-medium mb-3">
          Commission Calculation Preview
        </p>
        <p className="text-xs text-muted-foreground">
          No subscription plans available from Stripe.
        </p>
      </div>
    );
  }

  const validRate =
    !isNaN(commissionRate) && commissionRate >= 0 && commissionRate <= 100;

  return (
    <div className="mt-4 p-4 bg-muted/50 rounded-lg border">
      <p className="text-sm text-muted-foreground font-medium mb-1">
        Commission Calculation Preview
      </p>
      <p className="text-xs text-muted-foreground mb-3">
        Earnings per referred person per billing cycle
      </p>
      {!validRate ? (
        <p className="text-xs text-muted-foreground">
          Enter a valid commission rate (0-100) to see earnings breakdown.
        </p>
      ) : (
        <div className="space-y-3">
          {plans.map((plan) => {
            const price = plan.amount;
            const influencerEarning = (price * commissionRate) / 100;
            const ourEarning = price - influencerEarning;

            return (
              <div
                key={plan.id}
                className="p-3 bg-background rounded-md border space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{plan.name}</span>
                  <span className="text-sm text-muted-foreground">
                    ${price.toFixed(2)}/{plan.interval}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 bg-green-500/10 rounded">
                    <p className="text-muted-foreground">Influencer Earning</p>
                    <p className="text-green-600 dark:text-green-400 font-semibold text-sm">
                      ${influencerEarning.toFixed(2)}
                      <span className="font-normal text-xs text-muted-foreground ml-1">
                        /person
                      </span>
                    </p>
                    <p className="text-muted-foreground">({commissionRate}%)</p>
                  </div>
                  <div className="p-2 bg-primary/10 rounded">
                    <p className="text-muted-foreground">Our Earning</p>
                    <p className="text-primary font-semibold text-sm">
                      ${ourEarning.toFixed(2)}
                      <span className="font-normal text-xs text-muted-foreground ml-1">
                        /person
                      </span>
                    </p>
                    <p className="text-muted-foreground">
                      ({(100 - commissionRate).toFixed(1)}%)
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActionDialog({
  dialog,
  reason,
  onReasonChange,
  onClose,
  onConfirm,
  isPending,
  showCommissionCalculation,
}: {
  dialog: { type: string | null; user: AffiliateUser | null };
  reason: string;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  showCommissionCalculation?: boolean;
}) {
  const { data: plans, isLoading: plansLoading } = useSubscriptionPlans();

  if (!dialog.type || !dialog.user) return null;

  const configs: Record<
    string,
    {
      title: string;
      description: string;
      confirmText: string;
      variant?: "destructive" | "default";
      showInput: boolean;
      inputLabel?: string;
      selectOptions?: { value: string; label: string }[];
      inputType?: "text" | "number";
    }
  > = {
    suspend: {
      title: "Suspend Account",
      description: `Are you sure you want to suspend ${dialog.user.name}'s account? They will lose access immediately.`,
      confirmText: "Suspend Account",
      variant: "destructive",
      showInput: true,
      inputLabel: "Reason for suspension",
      inputType: "text",
    },
    restore: {
      title: "Restore Account",
      description: `Are you sure you want to restore ${dialog.user.name}'s account?`,
      confirmText: "Restore Account",
      showInput: true,
      inputLabel: "Optional reason",
      inputType: "text",
    },
    delete: {
      title: "Delete User Data (GDPR)",
      description:
        "This action is irreversible. Proceed only for GDPR, legal, or severe policy violations.",
      confirmText: "Delete Permanently",
      variant: "destructive",
      showInput: true,
      inputLabel: "Deletion reason",
      selectOptions: [
        { value: "gdpr", label: "GDPR Request" },
        { value: "legal", label: "Legal Compliance" },
        { value: "policy_violation", label: "Policy Violation" },
      ],
    },
    addNote: {
      title: "Add Internal Note",
      description: "Notes are only visible to administrators.",
      confirmText: "Add Note",
      showInput: true,
      inputLabel: "Note content",
      inputType: "text",
    },
    setCommission: {
      title: "Set Commission Rate",
      description: `Set a custom commission percentage for ${dialog.user.name}.`,
      confirmText: "Save Commission",
      showInput: true,
      inputLabel: "Commission percentage (%)",
      inputType: "number",
    },
  };

  const config = configs[dialog.type];
  if (!config) return null;

  const isCommissionInvalid =
    dialog.type === "setCommission" &&
    (reason === "" ||
      isNaN(Number(reason)) ||
      Number(reason) < 0 ||
      Number(reason) > 100);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        {config.showInput && (
          <div className="space-y-2 py-4">
            <Label>{config.inputLabel}</Label>

            {config.selectOptions ? (
              <Select value={reason} onValueChange={onReasonChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {config.selectOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : config.inputType === "number" ? (
              <div className="space-y-1">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={reason}
                  onChange={(e) => onReasonChange(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="e.g. 15.5"
                />
                <p className="text-xs text-muted-foreground">
                  Enter a value between 0 and 100
                </p>
                {showCommissionCalculation && (
                  <CommissionCalculationPreview
                    commissionRate={Number(reason) || 0}
                    plans={plans}
                    isLoading={plansLoading}
                  />
                )}
              </div>
            ) : (
              <Textarea
                value={reason}
                onChange={(e) => onReasonChange(e.target.value)}
                placeholder="Provide context..."
              />
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={config.variant || "default"}
            onClick={onConfirm}
            disabled={
              isPending ||
              (config.showInput &&
                dialog.type !== "restore" &&
                dialog.type !== "setCommission" &&
                !reason.trim()) ||
              isCommissionInvalid
            }
          >
            {isPending ? "Processing..." : config.confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
